import { inject, Injectable, NgZone } from '@angular/core';
import {
  loadCheckoutWebComponents,
  CheckoutWebComponents,
  Component as FlowComponent,
  CheckoutError,
  CheckoutErrorType,
  CheckoutRequestErrorCode,
  CheckoutSubmitErrorCode,
  PayPaymentSessionSuccessfulResponse,
} from '@checkout.com/checkout-web-components';
import { catchError, map, Observable, of } from 'rxjs';
import { CheckoutV2PaymentSession, CheckoutV2ValidatePayload, MastercardPayload } from '../models/payment.model';
import { PayAtTableNiPayload } from '../models/pay-at-table.model';
import { ApiService } from './api.service';

export interface CheckoutV2SessionDetails {
  publicKey: string;
  environment: 'sandbox' | 'production';
  transactionId: string;
  paymentSessionId: string;
  paymentSession: CheckoutV2PaymentSession;
}

export interface CheckoutV2ValidateParams {
  paymentId: string;
  paymentSessionId: string;
  transactionId?: string | null;
  chequeId?: string | null;
}

export interface CheckoutV2FlowHandlers {
  onPaymentCompleted: (paymentId: string) => void;
  onDeclined: () => void;
  onFatalError: () => void;
}

// A decline is a normal outcome (nothing charged) — anything else is treated as fatal.
export function classifyCheckoutError(error: CheckoutError): 'declined' | 'ignore' | 'fatal' {
  if (error.type === CheckoutErrorType.Request && error.code === CheckoutRequestErrorCode.PaymentRequestDeclined) {
    return 'declined';
  }
  if (error.type === CheckoutErrorType.Submit && error.code === CheckoutSubmitErrorCode.ComponentInvalid) {
    // Pay was clicked before all fields were filled in — Flow already highlights
    // the invalid/missing fields inline, so there's nothing for us to do here.
    return 'ignore';
  }
  return 'fatal';
}

@Injectable({
  providedIn: 'root',
})
export class CheckoutV2Service {
  private apiService = inject(ApiService);
  private ngZone = inject(NgZone);

  private checkout: CheckoutWebComponents | null = null;
  private flowComponent: FlowComponent | null = null;

  public getSessionDetails(payload: MastercardPayload | PayAtTableNiPayload): Observable<CheckoutV2SessionDetails> {
    return this.apiService.getCheckoutV2Session(payload).pipe(
      map((session) => ({
        publicKey: session.public_key,
        environment: session.environment,
        transactionId: session.transaction_id,
        paymentSessionId: session.payment_session.id,
        paymentSession: session.payment_session,
      }))
    );
  }

  public async mount(containerId: string, sessionDetails: CheckoutV2SessionDetails, handlers: CheckoutV2FlowHandlers): Promise<void> {
    this.checkout = await loadCheckoutWebComponents({
      publicKey: sessionDetails.publicKey,
      environment: sessionDetails.environment,
      paymentSession: sessionDetails.paymentSession,
      onPaymentCompleted: (_component, payment: PayPaymentSessionSuccessfulResponse) => handlers.onPaymentCompleted(payment.id),
      onError: (_component, error: CheckoutError) => {
        switch (classifyCheckoutError(error)) {
          case 'declined':
            handlers.onDeclined();
            break;
          case 'fatal':
            handlers.onFatalError();
            break;
          case 'ignore':
            break;
        }
      },
    });

    this.flowComponent = this.checkout.create('flow');
    this.flowComponent.mount(`#${containerId}`);
  }

  public unmount(): void {
    this.flowComponent?.unmount();
    this.flowComponent = null;
    this.checkout = null;
  }

  public validateCheckoutV2Session(params: CheckoutV2ValidateParams): Observable<boolean> {
    const payload: CheckoutV2ValidatePayload = {
      transaction_id: params.transactionId ?? null,
      payment_session_id: params.paymentSessionId,
      payment_id: params.paymentId,
    };
    if (params.chequeId) {
      payload.cheque_id = params.chequeId;
    }

    return this.apiService.validateCheckoutV2Transaction(payload).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
